import React, { useState, useEffect } from "react";
import { useGetAdminPageDataMutation } from "state/api";
import { DownloadOutlined } from "@mui/icons-material";
import { RingLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import * as XLSX from "xlsx";
import { useDispatch } from "react-redux";
import { setRmMgName } from "state/authSlice";
import { List } from "@mui/icons-material";
import ResetPassword from "./ResetPassword";

const Admin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [getAdminPageData, { data: rawData, isLoading, error }] =
    useGetAdminPageDataMutation();

  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedProduct, setSelectedProduct] = useState(""); // All products by default
  const [selectedDay, setSelectedDay] = useState(""); // All days by default
  const [uniqueProducts, setUniqueProducts] = useState([]); // Dynamic products from data

  // Fetch data on component mount and when month/year changes
  useEffect(() => {
    console.log(
      "Calling getAdminPageData with month:",
      selectedMonth,
      "year:",
      selectedYear
    );
    getAdminPageData({
      month: selectedMonth,
      year: selectedYear,
    })
      .then((result) => {
        console.log("Full API Response:", result);
        console.log("Response.data:", result?.data);
        console.log("Response.error:", result?.error);
      })
      .catch((error) => {
        console.error("API Error:", error);
      });
  }, [selectedMonth, selectedYear, getAdminPageData]);

  // Process data for display
  useEffect(() => {
    console.log("Raw data received:", rawData);
    console.log("Raw data type:", typeof rawData);
    console.log("Is array?", Array.isArray(rawData));

    // RTK Query mutation returns data directly, not wrapped
    let dataToProcess = [];
    if (Array.isArray(rawData)) {
      dataToProcess = rawData;
    } else if (rawData?.data && Array.isArray(rawData.data)) {
      dataToProcess = rawData.data;
    }

    console.log("Data to process:", dataToProcess);

    // Extract unique products dynamically
    if (dataToProcess && dataToProcess.length > 0) {
      const products = [...new Set(dataToProcess.map((item) => item.product))]
        .filter(Boolean)
        .sort();
      setUniqueProducts(products);
      console.log("Unique products found:", products);

      const filtered = dataToProcess.filter((item) => {
        const matchesSearch = item.RmMgName.toLowerCase().includes(
          searchTerm.toLowerCase()
        );
        const matchesProduct =
          selectedProduct === "" || item.product === selectedProduct;
        const matchesDay =
          selectedDay === "" ||
          new Date(item.Date).getDate().toString() === selectedDay;
        return matchesSearch && matchesProduct && matchesDay;
      });
      console.log("Filtered data count:", filtered.length);
      setFilteredData(filtered);
    } else {
      console.log("No data to display");
      setUniqueProducts([]);
      setFilteredData([]);
    }
  }, [rawData, searchTerm, selectedProduct, selectedDay]);

  const handleCardClick = (rmMgName) => {
    dispatch(setRmMgName(rmMgName));
    // Store month and year in localStorage for Amdetails to use
    localStorage.setItem("selectedMonth", selectedMonth);
    localStorage.setItem("selectedYear", selectedYear);
    navigate("/rm-details");
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Admin Data");
    XLSX.writeFile(
      workbook,
      `admin_data_${selectedMonth}_${selectedYear}.xlsx`
    );
  };

  const CustomSpinner = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#0f172a",
      }}
    >
      <RingLoader color="#3b82f6" size={100} />
    </Box>
  );

  if (isLoading) return <CustomSpinner />;

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 4 },
        maxWidth: "1440px",
        margin: "0 auto",
        minHeight: "100vh",
        background: "#0f172a",
      }}
    >
      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            "& .MuiTab-root": {
              color: "#94a3b8",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              "&.Mui-selected": {
                color: "#3b82f6",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#3b82f6",
            },
          }}
        >
          <Tab label="Dashboard" />
          <Tab label="Reset Password" />
        </Tabs>
      </Box>

      {/* Dashboard Tab */}
      {activeTab === 0 && (
        <>
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 3,
              mb: 4,
              padding: 3,
              borderRadius: 4,
              background: "linear-gradient(135deg, #3b82f6 0%, #ef4444 100%)",
              boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "white",
                flex: 1,
              }}
            >
              Daily Admin Dashboard
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: { xs: "100%", sm: "auto" },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                label="Month"
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                sx={{
                  width: 100,
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                    "&:hover fieldset": { borderColor: "white" },
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255,255,255,0.7)",
                  },
                  "& .MuiInputBase-input": { color: "white" },
                }}
              />
              <TextField
                label="Year"
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                sx={{
                  width: 120,
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                    "&:hover fieldset": { borderColor: "white" },
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255,255,255,0.7)",
                  },
                  "& .MuiInputBase-input": { color: "white" },
                }}
              />
              <Button
                onClick={downloadExcel}
                variant="contained"
                startIcon={<DownloadOutlined />}
                sx={{
                  py: 1,
                  px: 2.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  textTransform: "none",
                  background: "white",
                  color: "#ef4444",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                    background: "rgba(255,255,255,0.95)",
                  },
                  transition: "all 0.3s ease",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Export
              </Button>
            </Box>
          </Box>

          {/* Search Section */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Search RM Name"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by RmMgName..."
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  backgroundColor: "rgba(30, 41, 59, 0.8)",
                  "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
                "& .MuiOutlinedInput-input::placeholder": {
                  color: "rgba(255,255,255,0.5)",
                  opacity: 1,
                },
              }}
            />
            <FormControl
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  backgroundColor: "rgba(30, 41, 59, 0.8)",
                  "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
                "& .MuiInputBase-input": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
              }}
            >
              <InputLabel sx={{ color: "white" }}>Product</InputLabel>
              <Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                label="Product"
                sx={{
                  "& .MuiMenuItem-root": { color: "white" },
                  "& .MuiMenu-paper": { backgroundColor: "#1e293b" },
                }}
              >
                <MenuItem value="">All Products</MenuItem>
                {uniqueProducts.map((product) => (
                  <MenuItem key={product} value={product}>
                    {product}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  backgroundColor: "rgba(30, 41, 59, 0.8)",
                  "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
                "& .MuiInputBase-input": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
              }}
            >
              <InputLabel sx={{ color: "white" }}>Day</InputLabel>
              <Select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                label="Day"
                sx={{
                  "& .MuiMenuItem-root": { color: "white" },
                  "& .MuiMenu-paper": { backgroundColor: "#1e293b" },
                }}
              >
                <MenuItem value="">All Days</MenuItem>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day.toString()}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={() => navigate("/imagePreviewModal")}
              variant="contained"
              startIcon={<List />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: "bold",
                textTransform: "none",
                background: "#10b981",
                "&:hover": {
                  background: "#059669",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(16, 185, 129, 0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Pres images
            </Button>
            <Button
              onClick={() => navigate("/PobImagePreviewModal")}
              variant="contained"
              startIcon={<List />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: "bold",
                textTransform: "none",
                background: "#10b981",
                "&:hover": {
                  background: "#059669",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(16, 185, 129, 0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Pob images
            </Button>
            <Button
              onClick={() => navigate("/Dailywise")}
              variant="contained"
              startIcon={<List />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: "bold",
                textTransform: "none",
                background: "#10b981",
                "&:hover": {
                  background: "#059669",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(16, 185, 129, 0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Prescription
            </Button>
            <Button
              onClick={() => navigate("/DailyWisePob")}
              variant="contained"
              startIcon={<List />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: "bold",
                textTransform: "none",
                background: "#f59e0b",
                "&:hover": {
                  background: "#d97706",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(245, 158, 11, 0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              POB Score
            </Button>
          </Box>

          {/* Error State */}
          {error && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                flexDirection: "column",
                gap: 2,
                textAlign: "center",
                padding: 4,
              }}
            >
              <Typography variant="h5" sx={{ color: "#ef4444" }}>
                Error loading data
              </Typography>
              <Typography variant="body1" sx={{ color: "#94a3b8" }}>
                {error?.message || "Unable to fetch admin data"}
              </Typography>
              <Button
                variant="contained"
                onClick={() =>
                  getAdminPageData({ month: selectedMonth, year: selectedYear })
                }
                sx={{
                  background: "#3b82f6",
                  "&:hover": { background: "#2563eb" },
                }}
              >
                Try Again
              </Button>
            </Box>
          )}

          {/* Data Table */}
          {!error && filteredData.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                background: "#1e293b",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
                border: "1px solid rgba(59, 130, 246, 0.2)",
              }}
            >
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                      "& .MuiTableCell-head": {
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                      },
                    }}
                  >
                    <TableCell>RM Name</TableCell>
                    <TableCell align="center">Date</TableCell>
                    <TableCell align="center">Product</TableCell>
                    <TableCell align="center">Approved</TableCell>
                    <TableCell align="center">Rejected</TableCell>
                    <TableCell align="center">Pending</TableCell>
                    <TableCell align="center">Month</TableCell>
                    <TableCell align="center">Year</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? "rgba(59, 130, 246, 0.05)"
                            : "transparent",
                        borderBottom: "1px solid rgba(59, 130, 246, 0.1)",
                        "&:hover": {
                          backgroundColor: "rgba(59, 130, 246, 0.1)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <TableCell sx={{ color: "#f1f5f9", fontWeight: 600 }}>
                        {row.RmMgName}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#f1f5f9" }}>
                        {new Date(row.Date).toLocaleDateString("en-IN")}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color:
                            row.product === "Finease" ? "#3b82f6" : "#ef4444",
                          fontWeight: 600,
                        }}
                      >
                        {row.product}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "#10b981",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                        }}
                      >
                        {row.Approved}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "#ef4444",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                        }}
                      >
                        {row.Rejected}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "#f59e0b",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                        }}
                      >
                        {row.Pending}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#94a3b8" }}>
                        {row.Month}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#94a3b8" }}>
                        {row.Year}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleCardClick(row.RmMgName)}
                          sx={{
                            color: "#3b82f6",
                            fontWeight: 600,
                            "&:hover": {
                              background: "rgba(59, 130, 246, 0.1)",
                              color: "#60a5fa",
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Empty State */}
          {!error && filteredData.length === 0 && !isLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                textAlign: "center",
                padding: 4,
              }}
            >
              <Typography variant="h6" sx={{ color: "#94a3b8" }}>
                No data found for the selected month and year
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Reset Password Tab */}
      {activeTab === 1 && (
        <Box>
          <ResetPassword />
        </Box>
      )}
    </Box>
  );
};

export default Admin;
